import { useEffect, useRef, useState } from "react";

/** How far into the bottom-right corner the cursor counts as asking. */
const ZONE_PX = 140;
/** Held briefly after leaving, so crossing the corner does not flicker. */
const FADE_DELAY_MS = 800;

/**
 * Reveals a bottom-right affordance while the cursor is in the corner.
 *
 * A global pointermove rather than an absolutely-positioned hit area, so the
 * corner stays click-through to whatever is underneath while the control is
 * hidden. Two surfaces now want this behaviour, the demo panel and the
 * pre-login demo button, and they have to agree: an operator who learns the
 * corner on one screen expects the same corner on the other.
 *
 * `active` is false while the caller is showing something bigger of its own,
 * which is when tracking the cursor would only cost work.
 */
export function useCornerReveal(active = true): [boolean, (revealed: boolean) => void] {
  const [revealed, setRevealed] = useState(false);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const handler = (e: PointerEvent) => {
      const inZone =
        e.clientX > window.innerWidth - ZONE_PX && e.clientY > window.innerHeight - ZONE_PX;
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
      if (inZone) {
        setRevealed(true);
        return;
      }
      hideTimer.current = window.setTimeout(() => setRevealed(false), FADE_DELAY_MS);
    };
    window.addEventListener("pointermove", handler);
    return () => {
      window.removeEventListener("pointermove", handler);
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, [active]);

  return [revealed, setRevealed];
}
